<template>
  <el-container :class="['layout', { 'layout-mobile': isMobile }]" v-if="$route.name !== 'login'">
    <el-aside v-if="!isMobile" width="220px" class="aside">
      <AppSidebar
        :current-user="currentUser"
        :message-count="messageCount"
        :approval-count="approvalCount"
        :route-path="$route.path"
        @command="handleCommand"
      />
    </el-aside>

    <el-drawer
      v-model="mobileMenuOpen"
      direction="ltr"
      size="82%"
      :with-header="false"
      class="mobile-drawer"
    >
      <AppSidebar
        :current-user="currentUser"
        :message-count="messageCount"
        :approval-count="approvalCount"
        :route-path="$route.path"
        @command="handleSidebarCommand"
      />
    </el-drawer>

    <el-container class="main-container">
      <el-header class="header">
        <div class="header-left">
          <el-button v-if="isMobile" class="menu-toggle" circle @click="mobileMenuOpen = true">
            <el-icon><Operation /></el-icon>
          </el-button>
          <h2 class="page-title">{{ getPageTitle }}</h2>
        </div>
        <div class="header-right">
          <div class="quick-actions">
            <el-tooltip content="AI助手" placement="bottom">
              <el-button class="quick-btn" circle @click="router.push('/ai')">
                <el-icon><MagicStick /></el-icon>
              </el-button>
            </el-tooltip>
            <el-tooltip content="消息中心" placement="bottom">
              <el-button class="quick-btn" circle @click="router.push('/messages')">
                <el-icon><ChatDotRound /></el-icon>
                <span v-if="messageCount > 0" class="quick-badge">{{ messageCount > 99 ? '99+' : messageCount }}</span>
              </el-button>
            </el-tooltip>
          </div>

          <el-dropdown @command="handleCommand" trigger="click">
            <div class="user-panel">
              <el-avatar :size="32" class="header-avatar">
                {{ currentUser?.name?.charAt(0) || '用户' }}
              </el-avatar>
              <span v-if="!isMobile" class="header-username">{{ currentUser?.name || '用户' }}</span>
              <el-icon v-if="!isMobile" class="header-arrow"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <el-icon><User /></el-icon>
                  个人中心
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided>
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>

      <el-main class="main-content">
        <router-view />
      </el-main>
    </el-container>
  </el-container>

  <router-view v-else />
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import dayjs from 'dayjs'
import api from '@/api'
import { ElMessage, ElMessageBox } from 'element-plus'

import {
  ChatDotRound,
  User,
  ArrowDown,
  SwitchButton,
  MagicStick,
  Operation
} from '@element-plus/icons-vue'
import AppSidebar from '@/components/AppSidebar.vue'

const router = useRouter()
const route = useRoute()
const approvalCount = ref(0)
const messageCount = ref(0)
const currentUser = ref(null)
const isMobile = ref(window.innerWidth <= 768)
const mobileMenuOpen = ref(false)

// 页面标题映射
const pageTitleMap = {
  '/': '首页',
  '/messages': '消息中心',
  '/ai': 'AI助手',
  '/tasks': '任务管理',
  '/approvals': '审批管理',
  '/documents': '文档中心',
  '/calendar': '日历视图',
  '/equipments': '设备列表',
  '/categories': '设备分类',
  '/borrows': '借用管理',
  '/profiles': '用户管理',
  '/audit-logs': '审计日志',
  '/profile': '个人中心'
}

const getPageTitle = computed(() => {
  // 根据路由路径和用户角色动态返回标题
  const path = route.path
  if (path === '/tasks' && currentUser.value) {
    return ['管理员', '教师'].includes(currentUser.value.role) ? '任务管理' : '我的任务'
  }
  if (path === '/approvals' && currentUser.value) {
    return ['管理员', '教师'].includes(currentUser.value.role) ? '审批管理' : '我的申请'
  }
  return pageTitleMap[path] || '实验室管理系统'
})

watch(getPageTitle, (title) => {
  if (typeof document !== 'undefined') {
    document.title = title ? `${title} - 实验室管理系统` : '实验室管理系统'
  }
}, { immediate: true })

watch(() => route.path, () => {
  mobileMenuOpen.value = false
  if (route.name !== 'login' && !currentUser.value) {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      currentUser.value = JSON.parse(userStr)
    }
  }
})

const handleCommand = (command) => {
  if (command === 'logout') {
    handleLogout()
  } else if (command === 'profile') {
    router.push('/profile')
  }
}

const handleSidebarCommand = (command) => {
  mobileMenuOpen.value = false
  handleCommand(command)
}

const handleLogout = () => {
  ElMessageBox.confirm('确定要退出登录吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    localStorage.removeItem('user')
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    currentUser.value = null
    router.push('/login')
    ElMessage.success('已退出登录')
  }).catch(() => {})
}

const checkApprovals = async () => {
  try {
    if (!currentUser.value) return
    const params = { status: 'pending' }
    const currentProfileId = currentUser.value.id
    if (['管理员', '教师'].includes(currentUser.value.role)) {
      params.approver = currentProfileId
    } else {
      params.applicant = currentProfileId
    }
    const res = await api.getApprovals(params)
    const approvals = res.data.results || res.data || []
    approvalCount.value = approvals.length
  } catch (error) {
    console.error('检查审批失败', error)
  }
}

const checkMessages = async () => {
  try {
    if (!currentUser.value) return
    const res = await api.getMessageStats()
    // 后端返回的数据结构是 { stats: { unread_count: X, ... } }
    if (res.data && res.data.stats) {
      messageCount.value = res.data.stats.unread_count
    } else {
      // 降级处理：如果没有stats接口，则手动计算（兼容旧逻辑）
      const profileId = currentUser.value.id
      const listRes = await api.getMessages(profileId)
      const messages = listRes.data.results || listRes.data || []
      messageCount.value = messages.filter((m) => !m.is_read).length
    }
  } catch (error) {
    console.error('检查消息失败', error)
  }
}

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768
  if (!isMobile.value) {
    mobileMenuOpen.value = false
  }
}

onMounted(() => {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    currentUser.value = JSON.parse(userStr)
  }

  checkApprovals()
  checkMessages()

  window.addEventListener('approval-updated', checkApprovals)
  window.addEventListener('message-updated', () => {
    // 稍微延迟一下，确保后端事务已提交
    setTimeout(checkMessages, 200)
  })

  window.addEventListener('resize', handleResize)

  setInterval(() => {
    checkApprovals()
    checkMessages()
  }, 30 * 1000)
})
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'PingFang SC', 'Helvetica Neue', Helvetica, 'Microsoft YaHei', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.layout {
  height: 100vh;
  max-width: 100vw;
  background: #f4f5f7;
  overflow: hidden;
}

.aside {
  background: #fff;
  border-right: 1px solid #e8e8e8;
  overflow: hidden;
}
.main-container {
  flex-direction: column;
  min-width: 0;
}

.header {
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2329;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.menu-toggle {
  flex-shrink: 0;
  border: none;
  background: #f0f2f5;
  color: #1f2329;
}

.quick-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quick-btn {
  position: relative;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: #f0f2f5;
  color: #646a73;
  border-radius: 8px;
  transition: all 0.2s;
}

.quick-btn:hover {
  background: #e6f0ff;
  color: #3370ff;
}

.quick-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: #3370ff;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
}

.user-panel {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 6px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.user-panel:hover {
  background: #f0f2f5;
}

.header-avatar {
  background: linear-gradient(135deg, #3370ff 0%, #0057ff 100%);
  font-size: 12px;
  font-weight: 600;
}

.header-username {
  font-size: 14px;
  font-weight: 500;
  color: #1f2329;
}

.header-arrow {
  color: #646a73;
  font-size: 12px;
}

.main-content {
  flex: 1;
  background: #f4f5f7;
  padding: 20px;
  overflow-y: auto;
}

.el-dropdown-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  padding: 10px 16px;
}

.el-dropdown-menu__item .el-icon {
  font-size: 16px;
}

.el-dropdown-menu__item--divided {
  margin-top: 4px;
  border-top: 1px solid #f0f0f0;
}

.mobile-drawer :deep(.el-drawer__body) {
  padding: 0;
}

@media (max-width: 768px) {
  .layout {
    height: 100dvh;
  }

  .header {
    padding: 0 12px;
  }

  .header-right {
    gap: 8px;
  }

  .quick-actions {
    gap: 6px;
  }

  .quick-btn {
    width: 32px;
    height: 32px;
  }

  .user-panel {
    padding: 0;
  }

  .main-content {
    padding: 12px;
  }

  .el-dialog {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
    margin: 12px auto !important;
    display: flex;
    flex-direction: column;
  }

  .el-dialog__header,
  .el-dialog__footer {
    flex-shrink: 0;
  }

  .el-dialog__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .el-form:not(.el-form--inline) {
    max-width: 100%;
  }

  .el-form:not(.el-form--inline) .el-form-item {
    display: block;
  }

  .el-form:not(.el-form--inline) .el-form-item__label {
    width: auto !important;
    display: block;
    text-align: left;
    line-height: 1.4;
    margin-bottom: 6px;
  }

  .el-form:not(.el-form--inline) .el-form-item__content {
    margin-left: 0 !important;
    min-width: 0;
  }

  .el-input,
  .el-select,
  .el-date-editor,
  .el-input-number,
  .el-textarea {
    width: 100% !important;
    max-width: 100%;
  }

  .el-input__inner,
  .el-textarea__inner {
    font-size: 16px;
  }

  .el-select-dropdown,
  .el-picker-panel {
    max-width: calc(100vw - 24px);
  }
}
</style>
